import { User, Phone, MapPin, Search, UserPlus, Users, X, Check } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerStep({ data, update, onMeasurementsUpdate }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [newProfile, setNewProfile] = useState({ name: "", relationship: "" });
  const debounceRef = useRef(null);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      axios.get(`${API}/customers?q=${encodeURIComponent(query)}`).then((r) => setResults(r.data)).catch(() => {});
    }, 300);
  }, [query]);

  useEffect(() => {
    if (data.customer_id && !selectedCustomer) {
      axios.get(`${API}/customers/${data.customer_id}`).then((r) => setSelectedCustomer(r.data)).catch(() => {});
    }
  }, [data.customer_id, selectedCustomer]);

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    update({ customer_id: customer.id, profile_id: "", profile_name: "", name: customer.name, contact: customer.contact, address: customer.address });
    if (customer.measurements && onMeasurementsUpdate) {
      const m = customer.measurements;
      if (Object.values(m).some((v) => v > 0)) onMeasurementsUpdate(m);
    }
    setQuery(""); setResults([]);
  };

  const selectProfile = (profile) => {
    update({ profile_id: profile.id, profile_name: profile.name });
    if (profile.measurements && onMeasurementsUpdate) {
      const m = profile.measurements;
      if (Object.values(m).some((v) => v > 0)) onMeasurementsUpdate(m);
    }
  };

  const selectMainProfile = () => {
    update({ profile_id: "", profile_name: "" });
    if (selectedCustomer?.measurements && onMeasurementsUpdate) {
      const m = selectedCustomer.measurements;
      if (Object.values(m).some((v) => v > 0)) onMeasurementsUpdate(m);
    }
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    update({ customer_id: "", profile_id: "", profile_name: "", name: "", contact: "", address: "" });
  };

  const addSubProfile = async () => {
    if (!selectedCustomer || !newProfile.name) return;
    try {
      const res = await axios.post(`${API}/customers/${selectedCustomer.id}/sub-profiles`, newProfile);
      setSelectedCustomer((prev) => ({ ...prev, sub_profiles: [...(prev.sub_profiles || []), res.data] }));
      setNewProfile({ name: "", relationship: "" });
      setShowAddProfile(false);
    } catch { /* silent */ }
  };

  return (
    <div className="animate-fade-in" data-testid="customer-step">
      <h3 className="font-['Space_Mono'] text-lg tracking-tight uppercase text-[#5C4D24] mb-1">Customer Profile</h3>
      <p className="text-sm text-[#8B7536] mb-6">Search existing or enter new client details</p>

      {/* Search */}
      <div className="relative mb-6 max-w-xl">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-0 top-1/2 -translate-y-1/2 text-[#9F7D23]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or phone..." data-testid="customer-search-input" className="tinkah-input pl-6 text-sm" />
        </div>
        {results.length > 0 && (
          <div className="absolute z-10 w-full bg-white border border-[#D6D6CF] mt-1 shadow-lg max-h-48 overflow-y-auto" data-testid="customer-search-results">
            {results.map((c) => (
              <button key={c.id} onClick={() => selectCustomer(c)} data-testid={`customer-result-${c.id}`} className="w-full text-left px-4 py-3 hover:bg-[rgba(159,125,35,0.06)] border-b border-[#D6D6CF] last:border-0 transition-colors">
                <span className="text-sm text-[#5C4D24] font-medium">{c.name}</span>
                <span className="text-xs text-[#8B7536] ml-2">{c.contact}</span>
                {c.sub_profiles?.length > 0 && <span className="font-['Space_Mono'] text-[10px] text-[#9F7D23] ml-2">+{c.sub_profiles.length} profiles</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Customer Banner */}
      {selectedCustomer && (
        <div className="flex items-center justify-between bg-[rgba(159,125,35,0.06)] border border-[#9F7D23] px-4 py-3 mb-6 max-w-xl" data-testid="selected-customer-banner">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#9F7D23]" />
            <span className="font-['Space_Mono'] text-xs uppercase tracking-widest text-[#9F7D23]">Returning: {selectedCustomer.name}</span>
          </div>
          <button onClick={clearCustomer} data-testid="clear-customer-btn" className="text-[#8B7536] hover:text-[#9F3A3A] transition-colors"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Form Fields */}
      <div className="space-y-6 max-w-xl">
        <div>
          <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] mb-2 flex items-center gap-2"><User className="w-3 h-3" /> Full Name *</label>
          <input type="text" value={data.name} onChange={(e) => update({ name: e.target.value })} placeholder="Enter customer name" data-testid="customer-name-input" className="tinkah-input text-base" />
        </div>
        <div>
          <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] mb-2 flex items-center gap-2"><Phone className="w-3 h-3" /> Contact Number</label>
          <input type="tel" value={data.contact} onChange={(e) => update({ contact: e.target.value })} placeholder="+971 XX XXX XXXX" data-testid="customer-contact-input" className="tinkah-input text-base" />
        </div>
        <div>
          <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] mb-2 flex items-center gap-2"><MapPin className="w-3 h-3" /> Address</label>
          <textarea value={data.address} onChange={(e) => update({ address: e.target.value })} placeholder="Delivery address" rows={2} data-testid="customer-address-input" className="tinkah-input resize-none text-base" style={{ borderBottom: "2px solid #D6D6CF" }} />
        </div>
      </div>

      {/* Sub-Profiles */}
      {selectedCustomer && (
        <div className="mt-8 max-w-xl" data-testid="sub-profiles-section">
          <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#9F7D23] block mb-3">Select Profile (measurements auto-fill)</label>
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={selectMainProfile} data-testid="main-profile-btn" className={`px-4 py-2 border font-['Space_Mono'] text-xs tracking-widest uppercase transition-all flex items-center gap-2 ${!data.profile_id ? "bg-[#9F7D23] text-white border-[#9F7D23]" : "border-[#D6D6CF] text-[#5C4D24] hover:border-[#9F7D23]"}`}>
              {!data.profile_id && <Check className="w-3 h-3" />} {selectedCustomer.name}
            </button>
            {(selectedCustomer.sub_profiles || []).map((sp) => (
              <button key={sp.id} onClick={() => selectProfile(sp)} data-testid={`profile-btn-${sp.id}`} className={`px-4 py-2 border font-['Space_Mono'] text-xs tracking-widest uppercase transition-all flex items-center gap-2 ${data.profile_id === sp.id ? "bg-[#9F7D23] text-white border-[#9F7D23]" : "border-[#D6D6CF] text-[#5C4D24] hover:border-[#9F7D23]"}`}>
                {data.profile_id === sp.id && <Check className="w-3 h-3" />} {sp.name} <span className="opacity-60 text-[9px]">({sp.relationship})</span>
              </button>
            ))}
            <button onClick={() => setShowAddProfile(!showAddProfile)} data-testid="add-profile-toggle" className="px-4 py-2 border border-dashed border-[#9F7D23] text-[#9F7D23] font-['Space_Mono'] text-xs tracking-widest uppercase hover:bg-[rgba(159,125,35,0.06)] transition-all flex items-center gap-1">
              <UserPlus className="w-3 h-3" /> Add
            </button>
          </div>
          {showAddProfile && (
            <div className="flex gap-3 items-end border border-[#D6D6CF] p-4" data-testid="add-profile-form">
              <div className="flex-1">
                <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-1">Name</label>
                <input value={newProfile.name} onChange={(e) => setNewProfile((p) => ({ ...p, name: e.target.value }))} data-testid="new-profile-name" className="tinkah-input text-sm" placeholder="e.g. Sara" />
              </div>
              <div className="flex-1">
                <label className="font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536] block mb-1">Relationship</label>
                <input value={newProfile.relationship} onChange={(e) => setNewProfile((p) => ({ ...p, relationship: e.target.value }))} data-testid="new-profile-relationship" className="tinkah-input text-sm" placeholder="e.g. Daughter" />
              </div>
              <button onClick={addSubProfile} disabled={!newProfile.name} data-testid="save-profile-btn" className="px-4 py-2 bg-[#9F7D23] text-white font-['Space_Mono'] text-xs uppercase disabled:opacity-50 hover:bg-[#8A6D1F] transition-all">Save</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
